import { Box, Checkbox, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRefinementList } from 'react-instantsearch';
import { Product_Label_Name_Enum } from '@kheops/graphql';
import SearchInput from '../../common/components/SearchInput';
import FilterDisplay, { FilterDisplayMode } from '../../search/filters/FilterDisplay';
import useNormalizedCurrentRefinements from '../../hooks/useNormalizedCurrentRefinements';
import { logoLabels } from '../../packaging/packaging-formatter';

interface ProductLabelsFilterProps {
  mode?: FilterDisplayMode;
}

export default function ProductLabelsFilter({ mode }: ProductLabelsFilterProps): React.JSX.Element {
  const { t } = useTranslation(['discovery']);
  const { items, refine } = useRefinementList({ limit: 100, attribute: 'labels' });
  const currentRefinements = useNormalizedCurrentRefinements('labels') as string[];
  const [itemQuery, setItemQuery] = useState('');

  const labelOptions = useMemo((): React.JSX.Element[] => {
    return items
      .map((item) => ({ ...logoLabels[item.value as Product_Label_Name_Enum], label: item.value }))
      .filter((label) => label.alt.toLowerCase().indexOf(itemQuery.toLowerCase()) === 0)
      .sort((labelA, labelB) => labelA.alt.localeCompare(labelB.alt))
      .map(({ label, alt }) => (
        <ListItemButton
          key={label}
          onClick={() => refine(label)}
        >
          <ListItemText primary={alt} />
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={currentRefinements.includes(label)}
            />
          </ListItemIcon>
        </ListItemButton>
      ));
  }, [items, currentRefinements, itemQuery]);

  return (
    <FilterDisplay
      label={t('discovery:labels')}
      activeItemCount={currentRefinements.length}
      mode={mode}
      name="labels"
      content={(
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SearchInput
            fullWidth
            value={itemQuery}
            onChange={setItemQuery}
            placeholder={t('discovery:search_a_label')}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="bodyMedium" color="secondary">{t('discovery:labels')}</Typography>
            <List>
              {labelOptions}
            </List>
          </Box>
        </Box>
      )}
    />
  );
}
